import { useState } from 'react';
import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';
import Checkbox from '@material-ui/core/Checkbox';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Typography from '@material-ui/core/Typography';
import { Table, TableColumn } from '@backstage/core-components';
import { usePermission } from '@backstage/plugin-permission-react';
import {
  ConfigChange,
  ServiceValues,
  ValidationIssue,
  compareEnvironments,
  configurePermissionFor,
  environmentLabel,
  releaseConfigurePermission,
  releaseConfigureProdPermission,
  validateChanges,
} from '@internal/backstage-plugin-release-common';
import { currentValue } from './draft';
import type { ConfigurePlan } from './useSetValues';

interface Row {
  workload: string;
  setting: string;
  from: string;
  to: string;
}

const columns: TableColumn<Row>[] = [
  { title: 'Workload', field: 'workload', width: '90px' },
  { title: 'Setting', field: 'setting' },
  { title: 'From', field: 'from' },
  { title: 'To', field: 'to', render: row => <strong>{row.to}</strong> },
];

function settingName(change: ConfigChange): string {
  switch (change.kind) {
    case 'configMap':
      return change.key;
    case 'replicaCount':
      return 'replicas';
    case 'resource':
      return change.field;
    default:
      return '';
  }
}

function shown(value: string | undefined): string {
  if (value === undefined) return '— (new)';
  if (value === '') return '(empty)';
  return value;
}

function rowOf(values: ServiceValues, change: ConfigChange): Row {
  return {
    workload: change.workload,
    setting: settingName(change),
    from: shown(currentValue(values, change)),
    to: shown(String(change.value)),
  };
}

interface Props {
  open: boolean;
  values: ServiceValues;
  changes: ConfigChange[];
  onClose: () => void;
  onConfirm: (plan: ConfigurePlan) => void;
}

/**
 * The last look before anything is written: every change grouped by the
 * environment it lands in, whether the operator may write there, and what
 * would be refused and why. One pull request per environment follows, and
 * nothing merges from here.
 */
export const ConfigureDialog = ({ open, values, changes, onClose, onConfirm }: Props) => {
  const [dryRun, setDryRun] = useState(false);

  // Both asked for unconditionally — hooks cannot be called behind a
  // condition — and each environment picks the one that applies.
  const canConfigure = usePermission({ permission: releaseConfigurePermission }).allowed;
  const canConfigureProd = usePermission({ permission: releaseConfigureProdPermission }).allowed;
  const granted: Record<string, boolean> = {
    [releaseConfigurePermission.name]: canConfigure,
    [releaseConfigureProdPermission.name]: canConfigureProd,
  };

  const environments = [...new Set(changes.map(change => change.environment))].sort(
    compareEnvironments,
  );
  const issues: ValidationIssue[] = validateChanges(changes, values);
  const denied = environments.filter(
    environment => !granted[configurePermissionFor(environment).name],
  );

  const count = `${changes.length} change${changes.length === 1 ? '' : 's'}`;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle disableTypography>
        <Typography variant="h6">
          Review {count} to {values.service}
        </Typography>
      </DialogTitle>

      <DialogContent dividers>
        {issues.length > 0 && (
          <Box pb={2}>
            {issues.map((issue, index) => (
              <Typography key={index} variant="body2" color="error">
                {issue.message}
              </Typography>
            ))}
          </Box>
        )}

        {environments.map(environment => {
          const required = configurePermissionFor(environment);
          const allowed = granted[required.name];
          const rows = changes
            .filter(change => change.environment === environment)
            .map(change => rowOf(values, change));
          return (
            <Box key={environment} pb={3}>
              <Box display="flex" alignItems="baseline" gridGap={8} pb={1}>
                <Typography variant="subtitle1">{environmentLabel(environment)}</Typography>
                <Typography variant="body2" color="textSecondary">
                  one pull request
                </Typography>
              </Box>
              {!allowed && (
                <Box pb={1}>
                  <Typography variant="body2" color="error">
                    You do not have permission to change configuration in{' '}
                    {environmentLabel(environment)}. This needs{' '}
                    <strong>{required.name}</strong>.
                  </Typography>
                </Box>
              )}
              <Table
                options={{ search: false, paging: false, toolbar: false, padding: 'dense' }}
                columns={columns}
                data={rows}
              />
            </Box>
          );
        })}

        <Typography variant="body2" color="textSecondary">
          Each environment gets its own pull request in gitops-platform, with
          you named as the requester. Nothing merges from here: dev and staging
          merge on green checks, production waits for its code owners.
        </Typography>
      </DialogContent>

      <DialogActions style={{ padding: '12px 24px' }}>
        <Box flexGrow={1}>
          <FormControlLabel
            style={{ marginLeft: -9 }}
            control={
              <Checkbox
                size="small"
                checked={dryRun}
                onChange={event => setDryRun(event.target.checked)}
              />
            }
            label={
              <Typography variant="body2" color="textSecondary">
                Dry run — validate and report, open nothing
              </Typography>
            }
          />
        </Box>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant={dryRun ? 'outlined' : 'contained'}
          color="primary"
          disabled={changes.length === 0 || issues.length > 0 || denied.length > 0}
          onClick={() => onConfirm({ service: values.service, changes, dryRun })}
        >
          {dryRun
            ? 'Run dry'
            : `Open ${environments.length} pull request${environments.length === 1 ? '' : 's'}`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
